// パフォーマー一覧(設計書 §3.5.3)

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiClientError } from '../../api/client';
import {
  PERFORMER_STATUS_LABELS,
  usePerformersList,
  type PerformerListItem,
  type PerformerStatus,
  type Plan,
} from '../../api/admin/performers';
import { AdminLayout } from '../../components/AdminLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

const PER_PAGE = 20;

const PLAN_OPTIONS: Array<{ value: Plan | ''; label: string }> = [
  { value: '', label: 'すべて' },
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
];

const STATUS_OPTIONS: Array<{ value: PerformerStatus | ''; label: string }> = [
  { value: '', label: 'すべて' },
  { value: 'active', label: '有効' },
  { value: 'pending', label: '承認待ち' },
  { value: 'disabled', label: '無効' },
];

export function AdminPerformersPage() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [plan, setPlan] = useState<Plan | ''>('');
  const [status, setStatus] = useState<PerformerStatus | ''>('');

  const query = usePerformersList({
    page,
    per_page: PER_PAGE,
    ...(q ? { q } : {}),
    ...(plan ? { plan } : {}),
    ...(status ? { status } : {}),
  });

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setQ(qDraft.trim());
  }

  function applyFilter(setter: () => void) {
    setPage(1);
    setter();
  }

  return (
    <AdminLayout title="パフォーマー" description="登録済みのパフォーマー一覧です。">
      <Card>
        <form onSubmit={applySearch} style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'flex-end' }}>
            <Input
              label="検索(メール / 表示名)"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="部分一致"
            />
            <div style={{ minWidth: 100, marginBottom: 16 }}>
              <Button type="submit" variant="secondary" style={{ padding: '9px 12px', fontSize: '11px' }}>
                検索
              </Button>
            </div>
          </div>
        </form>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select
            label="プラン"
            value={plan}
            onChange={(e) => applyFilter(() => setPlan(e.target.value as Plan | ''))}
          >
            {PLAN_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Select
            label="ステータス"
            value={status}
            onChange={(e) =>
              applyFilter(() => setStatus(e.target.value as PerformerStatus | ''))
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {query.isLoading && !query.data && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {query.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {query.error instanceof ApiClientError
              ? query.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}

      {query.data && query.data.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            該当するパフォーマーはいません。
          </p>
        </Card>
      )}

      {query.data && query.data.data.length > 0 && (
        <Card>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-tertiary)' }}>
                <Th>表示名 / メール</Th>
                <Th>プラン</Th>
                <Th>ステータス</Th>
                <Th>配信状況</Th>
                <Th>累計配信</Th>
                <Th>最終ログイン</Th>
                <Th>登録日</Th>
              </tr>
            </thead>
            <tbody>
              {query.data.data.map((p) => (
                <PerformerRow key={p.ulid} performer={p} />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {query.data && query.data.pagination.total_pages > 1 && (
        <Card>
          <Pagination
            page={query.data.pagination.page}
            totalPages={query.data.pagination.total_pages}
            total={query.data.pagination.total}
            onChange={setPage}
            disabled={query.isFetching}
          />
        </Card>
      )}
    </AdminLayout>
  );
}

function PerformerRow({ performer }: { performer: PerformerListItem }) {
  return (
    <tr style={{ borderTop: 'var(--border-default)' }}>
      <Td>
        <Link
          to={`/admin/performers/${performer.ulid}`}
          style={{ color: 'var(--color-text-primary)' }}
        >
          {performer.display_name}
        </Link>
        <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>
          {performer.email}
        </div>
      </Td>
      <Td style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{performer.plan}</Td>
      <Td>
        <StatusBadge status={performer.status} />
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {performer.current_session_state ?? '—'}
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {formatDuration(performer.total_streaming_seconds)}
      </Td>
      <Td style={{ color: 'var(--color-text-tertiary)' }}>
        {performer.last_login_at ? formatDate(performer.last_login_at) : '—'}
      </Td>
      <Td style={{ color: 'var(--color-text-tertiary)' }}>
        {formatDate(performer.created_at)}
      </Td>
    </tr>
  );
}

function StatusBadge({ status }: { status: PerformerStatus }) {
  const colors: Record<PerformerStatus, { fg: string; bg: string }> = {
    active: { fg: 'var(--color-success-text)', bg: 'rgba(34,197,94,0.12)' },
    pending: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
    disabled: { fg: 'var(--color-text-tertiary)', bg: 'rgba(255,255,255,0.04)' },
  };
  const c = colors[status];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 500,
        color: c.fg,
        background: c.bg,
        padding: '2px 8px',
        borderRadius: '3px',
      }}
    >
      {PERFORMER_STATUS_LABELS[status]}
    </span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        fontWeight: 500,
        padding: '8px',
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <td style={{ padding: '10px 8px', verticalAlign: 'top', ...style }}>{children}</td>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (next: number) => void;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
        {page} / {totalPages} ページ(全 {total} 件)
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page <= 1}
            onClick={() => onChange(page - 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            前へ
          </Button>
        </div>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page >= totalPages}
            onClick={() => onChange(page + 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            次へ
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (!seconds) return '0';
  const h = Math.floor(seconds / 3600);
  if (h > 0) return `${h} 時間`;
  const m = Math.floor(seconds / 60);
  return `${m} 分`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ja-JP');
}
